/**
 * Phone number validation utilities for frontend
 * Validates international phone numbers based on country-specific rules
 */

// Country phone number rules: { minLength, maxLength }
// minLength/maxLength = digits AFTER country code
const COUNTRY_PHONE_RULES = {
  // North America (NANP)
  '+1': { minLength: 10, maxLength: 10, name: 'US/Canada' },
  
  // Europe
  '+44': { minLength: 10, maxLength: 10, name: 'United Kingdom' },
  '+33': { minLength: 9, maxLength: 9, name: 'France' },
  '+49': { minLength: 10, maxLength: 11, name: 'Germany' },
  '+39': { minLength: 9, maxLength: 10, name: 'Italy' },
  '+34': { minLength: 9, maxLength: 9, name: 'Spain' },
  '+31': { minLength: 9, maxLength: 9, name: 'Netherlands' },
  '+32': { minLength: 8, maxLength: 9, name: 'Belgium' },
  '+41': { minLength: 9, maxLength: 9, name: 'Switzerland' },
  '+43': { minLength: 10, maxLength: 11, name: 'Austria' },
  '+45': { minLength: 8, maxLength: 8, name: 'Denmark' },
  '+46': { minLength: 9, maxLength: 10, name: 'Sweden' },
  '+47': { minLength: 8, maxLength: 8, name: 'Norway' },
  '+358': { minLength: 9, maxLength: 10, name: 'Finland' },
  '+353': { minLength: 9, maxLength: 9, name: 'Ireland' },
  '+351': { minLength: 9, maxLength: 9, name: 'Portugal' },
  '+30': { minLength: 10, maxLength: 10, name: 'Greece' },
  '+48': { minLength: 9, maxLength: 9, name: 'Poland' },
  '+420': { minLength: 9, maxLength: 9, name: 'Czech Republic' },
  '+421': { minLength: 9, maxLength: 9, name: 'Slovakia' },
  '+36': { minLength: 9, maxLength: 9, name: 'Hungary' },
  '+40': { minLength: 9, maxLength: 9, name: 'Romania' },
  '+359': { minLength: 8, maxLength: 9, name: 'Bulgaria' },
  '+385': { minLength: 8, maxLength: 9, name: 'Croatia' },
  '+386': { minLength: 8, maxLength: 8, name: 'Slovenia' },
  '+372': { minLength: 7, maxLength: 8, name: 'Estonia' },
  '+371': { minLength: 8, maxLength: 8, name: 'Latvia' },
  '+370': { minLength: 8, maxLength: 8, name: 'Lithuania' },
  '+380': { minLength: 9, maxLength: 9, name: 'Ukraine' },
  '+375': { minLength: 9, maxLength: 9, name: 'Belarus' },
  '+7': { minLength: 10, maxLength: 10, name: 'Russia/Kazakhstan' },
  '+90': { minLength: 10, maxLength: 10, name: 'Turkey' },
  
  // Asia
  '+86': { minLength: 11, maxLength: 11, name: 'China' },
  '+91': { minLength: 10, maxLength: 10, name: 'India' },
  '+81': { minLength: 10, maxLength: 10, name: 'Japan' },
  '+82': { minLength: 9, maxLength: 10, name: 'South Korea' },
  '+62': { minLength: 9, maxLength: 12, name: 'Indonesia' },
  '+60': { minLength: 9, maxLength: 10, name: 'Malaysia' },
  '+65': { minLength: 8, maxLength: 8, name: 'Singapore' },
  '+66': { minLength: 9, maxLength: 9, name: 'Thailand' },
  '+84': { minLength: 9, maxLength: 10, name: 'Vietnam' },
  '+63': { minLength: 10, maxLength: 10, name: 'Philippines' },
  '+852': { minLength: 8, maxLength: 8, name: 'Hong Kong' },
  '+886': { minLength: 9, maxLength: 9, name: 'Taiwan' },
  '+880': { minLength: 10, maxLength: 10, name: 'Bangladesh' },
  '+92': { minLength: 10, maxLength: 10, name: 'Pakistan' },
  
  // Middle East
  '+971': { minLength: 9, maxLength: 9, name: 'UAE' },
  '+966': { minLength: 9, maxLength: 9, name: 'Saudi Arabia' },
  '+972': { minLength: 9, maxLength: 9, name: 'Israel' },
  '+974': { minLength: 8, maxLength: 8, name: 'Qatar' },
  '+973': { minLength: 8, maxLength: 8, name: 'Bahrain' },
  '+968': { minLength: 8, maxLength: 8, name: 'Oman' },
  '+965': { minLength: 8, maxLength: 8, name: 'Kuwait' },
  
  // Africa
  '+20': { minLength: 10, maxLength: 10, name: 'Egypt' },
  '+27': { minLength: 9, maxLength: 9, name: 'South Africa' },
  '+234': { minLength: 10, maxLength: 10, name: 'Nigeria' },
  '+254': { minLength: 9, maxLength: 9, name: 'Kenya' },
  '+233': { minLength: 9, maxLength: 9, name: 'Ghana' },
  '+212': { minLength: 9, maxLength: 9, name: 'Morocco' },
  '+213': { minLength: 9, maxLength: 9, name: 'Algeria' },
  '+216': { minLength: 8, maxLength: 8, name: 'Tunisia' },
  
  // Oceania
  '+61': { minLength: 9, maxLength: 9, name: 'Australia' },
  '+64': { minLength: 8, maxLength: 10, name: 'New Zealand' },
  
  // Americas
  '+52': { minLength: 10, maxLength: 10, name: 'Mexico' },
  '+55': { minLength: 10, maxLength: 11, name: 'Brazil' },
  '+54': { minLength: 10, maxLength: 10, name: 'Argentina' },
  '+56': { minLength: 9, maxLength: 9, name: 'Chile' },
  '+57': { minLength: 10, maxLength: 10, name: 'Colombia' },
  '+51': { minLength: 9, maxLength: 9, name: 'Peru' },
  '+58': { minLength: 10, maxLength: 10, name: 'Venezuela' },
};

// Sorted country codes for prefix matching (longest first)
const SORTED_COUNTRY_CODES = Object.keys(COUNTRY_PHONE_RULES).sort((a, b) => b.length - a.length);

/**
 * Get phone validation rules for a country code
 * @param {string} countryCode - Country code (e.g., +1, +44)
 * @returns {Object|null} Rules or null if unknown
 */
export const getPhoneRules = (countryCode) => {
  return COUNTRY_PHONE_RULES[countryCode] || null;
};

/**
 * Validate a phone number (national part only, without country code)
 * @param {string} nationalNumber - Phone number without country code
 * @param {string} countryCode - Country code (e.g., +1, +44)
 * @returns {Object} { valid: boolean, error?: string, hint?: string }
 */
export const validatePhoneNumber = (nationalNumber, countryCode) => {
  if (!nationalNumber || typeof nationalNumber !== 'string') {
    return { valid: false, error: 'Phone number is required' };
  }
  
  // Clean the number - keep only digits
  const digitsOnly = nationalNumber.replace(/\D/g, '');
  
  if (digitsOnly.length === 0) {
    return { valid: false, error: 'Please enter your phone number' };
  }
  
  // Check for obviously fake patterns
  // All same digit (e.g., 1111111111)
  if (/^(\d)\1+$/.test(digitsOnly)) {
    return { valid: false, error: 'Please enter a real phone number' };
  }
  
  // Sequential digits (e.g., 1234567890 or 9876543210)
  const isSequential = (str) => {
    const ascending = '01234567890123456789';
    const descending = '98765432109876543210';
    return ascending.includes(str) || descending.includes(str);
  };
  if (digitsOnly.length >= 7 && isSequential(digitsOnly)) {
    return { valid: false, error: 'Please enter a real phone number' };
  }
  
  // Repeated pattern (e.g., 123123123)
  if (digitsOnly.length >= 6) {
    const halfLen = Math.floor(digitsOnly.length / 2);
    const firstHalf = digitsOnly.substring(0, halfLen);
    const secondHalf = digitsOnly.substring(halfLen, halfLen * 2);
    if (firstHalf === secondHalf && firstHalf.length >= 3) {
      return { valid: false, error: 'Please enter a real phone number' };
    }
  }
  
  // Get rules for this country
  const rules = COUNTRY_PHONE_RULES[countryCode];
  
  if (!rules) {
    // Unknown country code - do basic validation (7-15 digits)
    if (digitsOnly.length < 7) {
      return { valid: false, error: 'Phone number is too short' };
    }
    if (digitsOnly.length > 15) {
      return { valid: false, error: 'Phone number is too long' };
    }
    return { valid: true };
  }
  
  // Validate against country-specific rules
  if (digitsOnly.length < rules.minLength) {
    const expected = rules.minLength === rules.maxLength 
      ? `${rules.minLength} digits` 
      : `${rules.minLength}-${rules.maxLength} digits`;
    return { 
      valid: false, 
      error: `Too short for ${rules.name}`,
      hint: `Expected ${expected}`
    };
  }
  
  if (digitsOnly.length > rules.maxLength) {
    const expected = rules.minLength === rules.maxLength 
      ? `${rules.minLength} digits` 
      : `${rules.minLength}-${rules.maxLength} digits`;
    return { 
      valid: false, 
      error: `Too long for ${rules.name}`,
      hint: `Expected ${expected}`
    };
  }
  
  return { valid: true };
};

/**
 * Get placeholder/hint for phone input based on country
 * @param {string} countryCode - Country code (e.g., +1, +44)
 * @returns {string} Placeholder text
 */
export const getPhonePlaceholder = (countryCode) => {
  const rules = COUNTRY_PHONE_RULES[countryCode];
  if (!rules) return 'Enter your phone number';
  
  const digits = rules.minLength === rules.maxLength 
    ? rules.minLength 
    : `${rules.minLength}-${rules.maxLength}`;
  
  return `${digits} digits for ${rules.name}`;
};

/**
 * Format full phone number
 * @param {string} countryCode - Country code (e.g., +1)
 * @param {string} nationalNumber - National phone number
 * @returns {string} Full formatted number
 */
export const formatFullPhoneNumber = (countryCode, nationalNumber) => {
  const digitsOnly = nationalNumber.replace(/\D/g, '');
  return `${countryCode}${digitsOnly}`;
};

export { COUNTRY_PHONE_RULES };
